<script lang="ts">
	import { Heading } from "flowbite-svelte-v1";
	import { nanoid } from "nanoid";
	import { FormSchema, RuleSchema, type Form, type Rule } from "#gen/forms/v1/forms_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import ConditionCard from "#lib/components/page/components/form/conditionals/ConditionCard.svelte";
	import { clone, create } from "#lib/protobuf-compat.js";
	import Button from "#lib/ui/Button/index.js";
	import type { ClassValue } from "svelte/elements";

	interface Props {
		form: Form;
		class?: ClassValue;
		onChange?: (f: Form) => void;
		addRuleAtBottom?: boolean;
	}

	let { form, onChange, addRuleAtBottom = false, ...props }: Props = $props();
	let rules = $derived.by(() => {
		const s = $state(form.rules);
		return s;
	});

	function updateRules(r: Rule[]) {
		const toUpdate = clone(FormSchema, form);
		toUpdate.rules = r;
		onChange?.(toUpdate);
	}

	function onDuplicate(i: number) {
		const newRule = clone(RuleSchema, rules[i]);
		newRule.id = nanoid(6);
		rules.push(newRule);
		updateRules(rules);
	}

	function onRemove(i: number) {
		rules.splice(i, 1);
		updateRules(rules);
	}
	function addRule() {
		if (!rules) {
			rules = [];
		}
		rules.push(create(RuleSchema, { id: nanoid(6), schema: `{"anyOf":[{"allOf":[{}]}]}` }));
	}

	function changeRule(idx: number, rule: Rule) {
		rules[idx] = rule;
		updateRules(rules);
	}

	let fieldOptions = $derived(form.fields.map(f => ({ value: f.name, name: f.title, field: f })));
</script>

<div class={props.class}>
	<div class="flex flex-col gap-4">
		{#if addRuleAtBottom}
			<div class="flex flex-col gap-1">
				<P class="text-sm font-bold text-black">Dynamic form rules</P>
				<P size="sm" secondary>Show fields when rules were met.</P>
			</div>
		{:else}
			<div class="sticky top-0 z-10 flex w-full items-center justify-between gap-4 bg-white py-4">
				<div class="flex grow flex-col gap-1">
					<Heading tag="h2" class="grow text-lg font-semibold">Dynamic form rules</Heading>
					<P size="sm" secondary>Show fields when rules were met.</P>
				</div>
				<div class="flex-none">
					<Button class="flex-none" id="add-rule-button" color="alternative" data-action-name="form-rules-add-rule" data-action-title="Add rule" onclick={addRule}>
						<Icon name="plus" class="mr-2" />
						Add rule
					</Button>
				</div>
			</div>
		{/if}
		{#if addRuleAtBottom && form.fields.length === 0 && rules.length === 0}
			<div class="flex flex-col gap-1 rounded-lg border border-dashed p-8">
				<P size="sm" weight="semibold" align="center">Add form fields first</P>
				<P size="sm" align="center" secondary>Dynamic rules show and hide fields based on responses.<br />Head to the Content tab to add form elements before you can create rules.</P>
			</div>
		{:else}
			{#each rules as rule, index (rule.id)}
				<section role="form">
					<ConditionCard fields={fieldOptions} bind:rule={rules[index]} onChange={changeRule} {onRemove} {onDuplicate} {index} />
				</section>
			{:else}
				<div class="rounded-lg border p-6">
					<P size="sm" align="center" secondary>There are currently no rules configured</P>
				</div>
			{/each}
			{#if addRuleAtBottom}
				{#if form.fields.length === 0}
					<div class="rounded-lg border border-dashed p-4">
						<P size="sm" align="center" secondary>Add form fields in the Content tab to create more rules.</P>
					</div>
				{:else}
					<div>
						<Button id="add-rule-button" color="alternative" data-action-name="form-rules-add-rule" data-action-title="Add rule" onclick={addRule}>
							Add rule
							<Icon name="plus" class="ml-2 h-4 w-4" />
						</Button>
					</div>
				{/if}
			{/if}
		{/if}
	</div>
</div>
